import { QuestionnaireAnswerType } from '@mfw/common';

import type {
  ApiQuestionnaire,
  UiQuestionnaire,
  UiQuestionnaireForm,
} from '../../schemas/questionnaires.schema';

const CHOICE_ANSWER_TYPES: QuestionnaireAnswerType[] = [
  QuestionnaireAnswerType.SINGLE_SELECT,
  QuestionnaireAnswerType.MULTISELECT,
];

export function mapQuestionnaireFromApi(
  api: ApiQuestionnaire,
): UiQuestionnaire {
  return {
    id: api.id,
    completedResponseCount: api.completedResponseCount ?? 0,
    createdAt: api.createdAt,
    description: api.description,
    includeWithInvitation: api.includeWithInvitation,
    isOrg: api.isOrg,
    lockedAt: api.lockedAt,
    questionCount: api.questions?.length ?? 0,
    responseCount: api.responseCount ?? 0,
    status: api.status,
    title: api.title,
    updatedAt: api.updatedAt,
    userId: api.userId,
  };
}

export function mapQuestionnaireToApi(
  data: UiQuestionnaireForm,
  { includeQuestions = true }: { includeQuestions?: boolean } = {},
) {
  return {
    description: data.description,
    includeWithInvitation: data.includeWithInvitation,
    isOrg: data.isOrg,
    // The BE rejects any update carrying `questions` once a questionnaire is
    // locked, even if the questions themselves are unchanged — omit the key
    // entirely for locked questionnaires so metadata-only edits go through.
    ...(includeQuestions && {
      // Only the fields that belong to the question's own answer type are
      // sent. The BE rejects a payload carrying settings for a different type,
      // and the builder keeps values behind after a type switch — the inputs
      // that produced them are no longer rendered, so leaving them in would
      // dead-end the form on a 400 the user cannot clear.
      questions: data.questions.map((question, questionIndex) => ({
        answerType: question.answerType,
        description: question.description,
        isRequired: question.isRequired,
        // 6.11 — only a yes/no or choice question can be a key one
        isKeyQuestion: question.isKeyQuestion,
        ...(question.isKeyQuestion &&
        question.answerType === QuestionnaireAnswerType.YES_NO
          ? { stopAnswerBoolean: question.stopAnswerBoolean }
          : {}),
        options: CHOICE_ANSWER_TYPES.includes(question.answerType)
          ? question.options.map((option, optionIndex) => ({
              optionText: option.optionText,
              order: optionIndex,
              stopsAccess: question.isKeyQuestion ? option.stopsAccess : false,
            }))
          : [],
        order: questionIndex,
        questionText: question.questionText,
        ...(question.answerType === QuestionnaireAnswerType.SCALE
          ? {
              scaleMax: question.scaleMax,
              scaleMin: question.scaleMin,
              scaleStep: question.scaleStep,
              unitLabel: question.unitLabel,
            }
          : {}),
        ...(question.answerType === QuestionnaireAnswerType.NUMBER
          ? {
              numberMax: question.numberMax,
              numberMin: question.numberMin,
            }
          : {}),
      })),
    }),
    status: data.status,
    title: data.title,
  };
}
